import { parseSurgeryRequest } from "../../lib/formatters";
import { Surgery } from "../../types/patient.types";
import { Order, OrderStatus } from "./types";

export function deriveStatus(surgery: Surgery): OrderStatus {
  const incomplete = !surgery.hospital || !surgery.tipoCirurgia || !surgery.dataDesejada;
  if (incomplete) return 'incompleto';
  const missingPatient = !surgery.sexo || !surgery.convenio || !surgery.numCarteirinha || !surgery.valCarteirinha;
  if (missingPatient) return 'aguardando';
  return 'completo';
}

/** Nome do primeiro procedimento, sem o código e sem a quantidade ("30715016 Artroplastia x1" → "Artroplastia"). */
export function deriveProcedureName(surgery: Surgery): string {
  const firstProcedure = (surgery.procedimentos_validados ?? '').split(',')[0].trim();
  return firstProcedure.replace(/^\d+\s+/, '').replace(/[-\s]*x\d+$/, '').trim();
}

/** Pedidos do mais recente para o mais antigo; o id é o índice em `surgeries`. */
export function surgeriesToOrders(surgeries: Surgery[]): Order[] {
  return surgeries
    .map((s, i) => ({
      id: String(i),
      name: deriveProcedureName(s),
      timestamp: s.timestamp,
      status: deriveStatus(s),
      hasRecurso: Boolean(s.recurso),
    }))
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

export const countProcedures = (surgery: Surgery) =>
  (surgery.procedimentos_validados ?? '').split(',').filter((p) => p.trim()).length;

/** CIDs como aparecem no texto do pedido gerado (vazio se o pedido ainda não foi gerado). */
export const cidsOf = (pedido: string) => pedido?.match(/CID's:\s*([\s\S]*?)(?=\n?-{3,})/)?.[1]?.trim() ?? '';

function formatProceduresList(procedimentosValidados: string): string {
    if (!procedimentosValidados || !procedimentosValidados.trim()) {
      return '';
    }
  
    // 1. Separa a string por vírgulas para tratar cada procedimento individualmente
    const itens = procedimentosValidados.split(',');
  
    const linhasFormatadas = itens.map((item) => {
      const textoItem = item.trim();
  
      // 2. Regex mágico:
      // (^\\d+)       -> Grupo 1: Pega os dígitos do código no início
      // (.*?)         -> Grupo 2: Pega o nome do procedimento (tudo no meio de forma preguiçosa)
      // [-\\s]*x(\\d+)$ -> Grupo 3: Ignora espaços/hifens e pega apenas o número após o 'x' no fim
      const match = textoItem.match(/^(\d+)\s+(.*?)[-\s]*x(\d+)$/i);
  
      if (match) {
        const code = match[1];
        const nomeProcedimento = match[2].trim();
        const quantity = match[3];
  
        // 3. Retorna no formato solicitado: [code] [quantity] [nome do procedimento]
        return `${code} x${quantity} ${nomeProcedimento}`;
      }
  
      // Se algum item estiver fora do padrão, retorna ele limpo como fallback
      return textoItem;
    });
  
    // 4. Junta todas as linhas aplicando a quebra de linha
    return linhasFormatadas.join('\n');
  }

export const surgeryRequestDataExtract = (texto: string, desiredDate: string, opmeSummary: string, procedimentos_validados: string, hospital: string) =>
  parseSurgeryRequest(texto ?? '', { desiredDate, opmeSummary, procedures: formatProceduresList(procedimentos_validados), hospital });
